// components/hooks/useGateAnimation.js
import { useState, useCallback, useRef, useEffect } from "react";
import * as THREE from "three";

export function useGateAnimation(actions, onToggle, onAnimationFinished) {
  const [isGateOpened, setIsGateOpened] = useState(false);
  const gateSoundOpen = useRef(null);
  const gateSoundClose = useRef(null);

  const ensureSounds = useCallback(() => {
    if (typeof window === "undefined") return;
    if (!gateSoundOpen.current) {
      gateSoundOpen.current = new Audio("/open.mp3");
      gateSoundOpen.current.volume = 0.5;
    }
    if (!gateSoundClose.current) {
      gateSoundClose.current = new Audio("/close.mp3");
      gateSoundClose.current.volume = 0.5;
    }
  }, []);

  // Инициализация звуков один раз
  useEffect(() => {
    ensureSounds();
  }, [ensureSounds]);

  // Окончание анимации (в том числе при обратном проигрывании)
  useEffect(() => {
    const action = Object.values(actions || {})[0];
    if (!action) return;

    const mixer = action.getMixer();
    const handleFinished = (e) => {
      if (e.action !== action) return;
      onAnimationFinished?.(isGateOpened);
    };

    mixer.addEventListener("finished", handleFinished);
    return () => mixer.removeEventListener("finished", handleFinished);
  }, [actions, isGateOpened, onAnimationFinished]);

  const toggleGate = useCallback(
    (isOpen) => {
      const action = Object.values(actions || {})[0];
      if (!action) return 0;

      ensureSounds();
      const duration = action.getClip().duration;

      action.stop();
      [gateSoundOpen.current, gateSoundClose.current].forEach((s) => {
        if (!s) return;
        s.pause();
        s.currentTime = 0;
      });

      action.time = isOpen ? 0 : duration;
      action.setLoop(THREE.LoopOnce, 0);
      action.clampWhenFinished = true;
      action.timeScale = isOpen ? 1 : -1;
      action.play();

      const sound = isOpen ? gateSoundOpen.current : gateSoundClose.current;
      sound?.play().catch((e) => console.warn("Звук не проигрался:", e));

      setIsGateOpened(isOpen);
      onToggle?.(isOpen);

      // возвращаем длительность — нужна для страховочного таймера в UI
      return duration;
    },
    [actions, onToggle, ensureSounds],
  );

  return { isGateOpened, toggleGate };
}
